import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Paper, Typography, Box, CircularProgress } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import theme from '../theme/theme';

/**
 * Shared layout wrapper for all Add / Edit form pages.
 *
 * Props:
 *  - title       : Page heading (e.g. "ADD NEW USER")
 *  - backTo      : Route string or { path, state } object to navigate back to
 *  - loading     : Shows a full-page spinner when true (used for data-fetching state)
 *  - children    : The form content
 *  - paperPadding: Optional override for Paper padding (default: 3)
 */
const FormPageLayout = ({ title, backTo, loading = false, children, paperPadding = 3 }) => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (!backTo) return;
    if (typeof backTo === 'string') navigate(backTo);
    else navigate(backTo.path, { state: backTo.state });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress sx={{ color: '#008080' }} />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: paperPadding }}>
          {/* Back button */}
          {backTo && (
            <button
              onClick={handleBack}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                color: '#008080',
                fontSize: '16px',
                fontWeight: 500,
                cursor: 'pointer',
                marginBottom: '8px',
              }}
            >
              ← Back
            </button>
          )}

          {/* Header row: title + logo */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, mb: 2 }}>
            <Typography variant="h4" sx={{ color: '#008080', fontWeight: 600 }}>
              {title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img src="/logo.png" alt="Logo" style={{ width: 32, height: 32 }} />
              <Typography variant="h6" sx={{ color: '#008080', ml: 1, fontWeight: 500 }}>
                OTMS
              </Typography>
            </Box>
          </Box>

          {/* Page content */}
          {children}
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default FormPageLayout;
